import { PaymentInput } from "@/components/base/input/input-payment";
import { SUBSCRIPTION_COMPONENT_TRANSLATIONS as T } from "@/components/subscription/constants";
import { CardExpiryInput } from "@/components/base/input/inputCardExpiry";
import ClinicSetupStep from "@/components/subscription/ClinicSetupStep";
import PlanChangeConfirmStep from "@/components/subscription/PlanChangeConfirmStep";
import SecretaryMigrationStep from "@/components/subscription/SecretaryMigrationStep";
import {
    FreeBasicTrialConfirmStep,
    requiresFreeBasicTrialConfirmation,
} from "@/components/subscription/FreeBasicTrialConfirmStep";
import {
    requiresTrialUpgradeConfirmation,
    TrialPlanUpgradeConfirmStep,
} from "@/components/subscription/TrialPlanUpgradeConfirmStep";
import {
    fetchSoloSecretaryMigrationSummary,
    resolvePostPlanChangeLanding,
    resolveSecretaryMigrationBackStep,
    resolveStepAfterClinicReady,
    resolveStepAfterSecretaryMigration,
} from "@/components/subscription/subscriptionCheckoutPlanFlow";
import {
    useSoloSecretaryMigration,
} from "@/components/subscription/useSoloSecretaryMigration";
import { clinicApi } from "@/domains/multi/clinicApi";
import {
    checkoutCreditCardBilling,
    changeBillingPaymentMethodBilling,
    checkoutPixBilling,
    pollPixCheckoutBilling,
    simulatePixCheckoutBilling,
} from "@/domains/multi/checkoutBillingOperations";
import { CLINIC_BILLING_CHECKOUT_TRANSLATIONS as PlanFitT } from "@/domains/multi/clinicDialogConstants";
import { fetchCheckoutBillingSubscription } from "@/domains/multi/fetchCheckoutBillingSubscription";
import type { ClinicPlanFitBlocker, ClinicSetupPayload } from "@/domains/multi/clinicTypes";
import {
    canShowChangePaymentMethod,
    formatBrl,
    isManageableSubscription,
    shouldUseClinicBilling,
    subscriptionPixCheckoutEnabled,
    type BillingSubscriptionSummary,
} from "@/domains/subscription/billingHelpers";
import { Button } from "@/domains/shared/components/Button";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/domains/shared/components/Dialog";
import { Input } from "@/domains/shared/components/Input";
import { Label } from "@/domains/shared/components/Label";
import { authMeQueryKey, useAuth } from "@/domains/shared/hooks/useAuth";
import { Roles } from "@/domains/shared/models/roles";
import { describeApiError } from "@/domains/shared/services/api";
import { parseCardExpiryParts } from "@/domains/shared/validation/cardExpiryInput";
import { useSubscriptionCheckout } from "@/domains/subscription/SubscriptionCheckoutContext";
import { requiresPaymentBeforeAccess, THERAPIST_PROFILE_PATH } from "@/domains/subscription/accountAccess";
import {
    buildCreditCardHolderPayload,
    formatCardBillingProfileGapsList,
    getCardBillingProfileGaps,
    getCardBillingProfileGapsFromGatewayError,
    hasCardBillingProfile,
    isCardBillingProfileScrollHash,
    profileHashForFirstCardBillingGap,
    type CardBillingProfileGap,
} from "@/domains/subscription/creditCardHolder";
import { clearPlanIntent, setPlanIntent } from "@/domains/subscription/planIntent";
import {
    getPlanFromCatalog,
    isClinicKeepPlanDowngrade,
    isDowngradePlan,
    isUpgradePlan,
    normalizePlanSlug,
    selectableCheckoutPlans,
    SubscriptionPlanIds,
    type SubscriptionPlanId,
} from "@/domains/subscription/plans";
import { usePlanCatalog } from "@/domains/subscription/usePlanCatalog";
import { planAdjustmentNoticeQueryKey } from "@/domains/subscription/hooks/usePlanAdjustmentNotice";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { getTranslation } from "@mivo/i18n";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { captureProductEvent } from "@/monitoring/productAnalytics";
import { resolveAnalyticsPlanSlug } from "@/monitoring/resolveAnalyticsPlanSlug";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";

type Step = "plan" | "freeBasicTrialConfirm" | "trialUpgradeConfirm" | "clinicSetup" | "secretaryMigration" | "confirm" | "method" | "pix" | "card" | "success";

function PixLogoTile() {
    return (
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#32BCAD]/10 text-[#32BCAD] font-bold text-xs">
            Pix
        </span>
    );
}

function resolveInitialStep(
    mode: ReturnType<typeof useSubscriptionCheckout>["mode"],
    billing?: BillingSubscriptionSummary,
    preferredStep?: ReturnType<typeof useSubscriptionCheckout>["initialStep"],
): Step {
    if (preferredStep) {
        return preferredStep;
    }

    if (mode === "payment-method") {
        if (!subscriptionPixCheckoutEnabled || !canShowChangePaymentMethod(billing)) {
            return "plan";
        }
        return "method";
    }
    if (mode === "manage" || isManageableSubscription(billing)) {
        return "plan";
    }
    return "plan";
}

export function SubscriptionCheckoutModal() {
    const { open, initialPlanId, mode, initialStep, nonDismissable, conversionCheckout, closeCheckout, skipPendingCheckoutAutoOpenRef } =
        useSubscriptionCheckout();
    const { user, logout, refreshMe } = useAuth();
    const queryClient = useQueryClient();
    const billingQuery = useQuery({
        queryKey: ["billing", "subscription", user?.activeClinicId ?? null, user?.isMainManager ?? false],
        queryFn: () => fetchCheckoutBillingSubscription(user),
        enabled: open,
    });
    const { catalog } = usePlanCatalog();

    const billing = billingQuery.data;
    const isManage = mode === "manage" || (mode === "checkout" && isManageableSubscription(billing));
    const isPaymentMethodOnly = mode === "payment-method";
    const currentPlanId = (billing?.currentPlanId ?? billing?.subscriptionPlan) as SubscriptionPlanId | undefined;

    const [step, setStep] = useState<Step>("plan");
    const [planId, setPlanId] = useState<SubscriptionPlanId>(SubscriptionPlanIds.pro);
    const [changePaymentMethod, setChangePaymentMethod] = useState(false);
    const [selectedSecretaryUserId, setSelectedSecretaryUserId] = useState<string | null>(null);
    const [clinicSetupCompleted, setClinicSetupCompleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pixSessionId, setPixSessionId] = useState<string | null>(null);
    const [pixPayload, setPixPayload] = useState<string | null>(null);
    const [devSimulatePixAvailable, setDevSimulatePixAvailable] = useState(false);
    const [card, setCard] = useState({ holderName: "", number: "", expiryMonth: "", expiryYear: "", ccv: "" });
    const [cardExpiryDigits, setCardExpiryDigits] = useState("");
    const [holder, setHolder] = useState({
        name: "",
        email: "",
        cpfCnpj: "",
        addressNumber: "",
        mobilePhone: "",
    });
    const [showBillingProfileModal, setShowBillingProfileModal] = useState(false);
    const [billingProfileGaps, setBillingProfileGaps] = useState<CardBillingProfileGap[]>([]);
    const [billingProfileCountdown, setBillingProfileCountdown] = useState(10);
    const [fitBusy, setFitBusy] = useState(false);
    const [remediationOpen, setRemediationOpen] = useState(false);
    const [remediationBlockers, setRemediationBlockers] = useState<ClinicPlanFitBlocker[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const userSelectedPlan = useRef(false);
    const wasOpenRef = useRef(false);

    const isOnProfileBillingSection =
        location.pathname.replace(/\/+$/, "") === THERAPIST_PROFILE_PATH
        && isCardBillingProfileScrollHash(location.hash);

    const selectedPlan = useMemo(
        () => getPlanFromCatalog(catalog, planId),
        [catalog, planId],
    );

    const displayPlan = useMemo(() => {
        if (isPaymentMethodOnly && currentPlanId) {
            return getPlanFromCatalog(catalog, currentPlanId);
        }
        return selectedPlan;
    }, [isPaymentMethodOnly, currentPlanId, catalog, selectedPlan]);

    const effectiveCurrentPlanId = normalizePlanSlug(
        currentPlanId
        ?? billing?.subscriptionPlan
        ?? (billingQuery.isPending ? user?.subscriptionPlan : undefined)
        ?? SubscriptionPlanIds.basic,
    ) as SubscriptionPlanId;
    const paymentLock = requiresPaymentBeforeAccess(
        user
            ? {
                role: Roles.Therapist,
                subscriptionPlan: user.subscriptionPlan,
                pendingPaidPlanId: user.pendingPaidPlanId,
            }
            : null,
    );
    const selectablePlans = useMemo(
        () => selectableCheckoutPlans(catalog, isManage, paymentLock, conversionCheckout),
        [catalog, isManage, paymentLock, conversionCheckout],
    );
    const planChanged = isManage && planId !== effectiveCurrentPlanId;
    const clinicUpgradeCandidate =
        open
        && isManage
        && isUpgradePlan(effectiveCurrentPlanId, planId)
        && planId === SubscriptionPlanIds.clinic;
    const needsClinicSetup = clinicUpgradeCandidate && !user?.activeClinicId;
    const useClinicBilling = shouldUseClinicBilling(user);
    const soloSecretariesQuery = useSoloSecretaryMigration(
        user?.id,
        clinicUpgradeCandidate && !needsClinicSetup,
        useClinicBilling,
    );
    const awaitingSecretaryMigrationData =
        clinicUpgradeCandidate && !needsClinicSetup && soloSecretariesQuery.isLoading;
    const canContinueFromPlan =
        !isManage
        || changePaymentMethod
        || planChanged;
    const continueDisabled =
        !canContinueFromPlan
        || awaitingSecretaryMigrationData
        || planId === effectiveCurrentPlanId
        || fitBusy;
    const hasActivePaidSubscription = isManageableSubscription(billing);
    const upgradingFromBasicTrial = requiresTrialUpgradeConfirmation(
        effectiveCurrentPlanId,
        planId,
        hasActivePaidSubscription,
    );
    const canStartBasicTrial = user?.canStartBasicTrial ?? billing?.canStartBasicTrial ?? false;
    const upgradingFromFreeBasicTrial = requiresFreeBasicTrialConfirmation(
        effectiveCurrentPlanId,
        planId,
        canStartBasicTrial,
    );
    const devFakeBillingAvailable = Boolean(billing?.devFakeBillingAvailable);
    const useManageBillingApis = hasActivePaidSubscription && (isPaymentMethodOnly || isManage) && !upgradingFromBasicTrial;
    const pixMethodEnabled =
        subscriptionPixCheckoutEnabled
        && !upgradingFromFreeBasicTrial;
    const showChangePaymentMethodOption = canShowChangePaymentMethod(billing);

    useEffect(() => {
        if (!open || !isPaymentMethodOnly || subscriptionPixCheckoutEnabled) {
            return;
        }

        toast.error(getTranslation(T.paymentMethodChangeUnavailable));
        closeCheckout();
    }, [open, isPaymentMethodOnly, closeCheckout]);

    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            userSelectedPlan.current = false;
            setClinicSetupCompleted(false);
            return;
        }

        const opening = !wasOpenRef.current;
        wasOpenRef.current = true;

        if (!opening) {
            return;
        }

        setStep(resolveInitialStep(mode, billing, initialStep));
        setChangePaymentMethod(isPaymentMethodOnly && canShowChangePaymentMethod(billing));
        setPixSessionId(null);
        setPixPayload(null);
        setDevSimulatePixAvailable(false);
        setCard({ holderName: "", number: "", expiryMonth: "", expiryYear: "", ccv: "" });
        setCardExpiryDigits("");

        if (initialPlanId && selectablePlans.some((plan) => plan.id === initialPlanId)) {
            setPlanId(initialPlanId);
            return;
        }

        const defaultPlanId =
            selectablePlans.find((plan) => plan.id === effectiveCurrentPlanId)?.id
            ?? selectablePlans.find((plan) => plan.id === SubscriptionPlanIds.pro)?.id
            ?? selectablePlans[0]?.id
            ?? SubscriptionPlanIds.pro;
        setPlanId(defaultPlanId);
    }, [open, initialPlanId, mode, initialStep, isManage, isPaymentMethodOnly, selectablePlans, effectiveCurrentPlanId, billing]);

    useEffect(() => {
        if (!open || userSelectedPlan.current || selectablePlans.length === 0) {
            return;
        }

        if (selectablePlans.some((plan) => plan.id === planId)) {
            return;
        }

        const fallbackPlanId =
            selectablePlans.find((plan) => plan.id === effectiveCurrentPlanId)?.id
            ?? (initialPlanId && selectablePlans.some((plan) => plan.id === initialPlanId)
                ? initialPlanId
                : null)
            ?? selectablePlans[0]?.id
            ?? SubscriptionPlanIds.pro;
        setPlanId(fallbackPlanId);
    }, [open, selectablePlans, planId, effectiveCurrentPlanId, initialPlanId]);

    useEffect(() => {
        if (!open || userSelectedPlan.current || initialPlanId || !isManage || !billing) {
            return;
        }

        if (!selectablePlans.some((plan) => plan.id === effectiveCurrentPlanId)) {
            return;
        }

        setPlanId(effectiveCurrentPlanId);
    }, [open, billing, effectiveCurrentPlanId, isManage, initialPlanId, selectablePlans]);

    useEffect(() => {
        if (step === "success") {
            clearPlanIntent();
        }
    }, [step]);

    useEffect(() => {
        if (step !== "card" || !user) {
            return;
        }

        setHolder({
            name: user.name ?? "",
            email: user.email ?? "",
            cpfCnpj: "",
            addressNumber: user.streetNumber ?? "",
            mobilePhone: "",
        });
    }, [step, user]);

    useEffect(() => {
        if (step !== "pix" || !pixSessionId) {
            return;
        }

        const interval = window.setInterval(async () => {
            try {
                const status = await pollPixCheckoutBilling(useClinicBilling, pixSessionId);
                setDevSimulatePixAvailable(Boolean(status.devSimulatePixAvailable));
                if (status.authorizationStatus === "refused") {
                    window.clearInterval(interval);
                    toast.error(getTranslation(T.pixAuthorizationRefused));
                    setPixSessionId(null);
                    setPixPayload(null);
                    setStep("method");
                    return;
                }
                if (status.isPaid) {
                    window.clearInterval(interval);
                    skipPendingCheckoutAutoOpenRef.current = true;
                    await invalidateBilling();
                    setStep("success");
                    toast.success(getTranslation(T.paymentConfirmed));
                    closeCheckout();
                }
            } catch {
                // polling fallback only
            }
        }, 3000);

        return () => window.clearInterval(interval);
    }, [step, pixSessionId, queryClient]);

    const goToProfileForBillingGap = useCallback(() => {
        skipPendingCheckoutAutoOpenRef.current = true;
        setShowBillingProfileModal(false);
        const profileHash = profileHashForFirstCardBillingGap(billingProfileGaps);
        navigate(`${THERAPIST_PROFILE_PATH}${profileHash}`, { replace: true });
        closeCheckout();
    }, [billingProfileGaps, closeCheckout, navigate, skipPendingCheckoutAutoOpenRef]);

    useLayoutEffect(() => {
        if (!open || !isOnProfileBillingSection) {
            return;
        }

        setShowBillingProfileModal(false);
        closeCheckout();
    }, [open, isOnProfileBillingSection, closeCheckout]);

    useEffect(() => {
        if (!showBillingProfileModal) {
return;
}
        if (billingProfileCountdown <= 0) {
            goToProfileForBillingGap();
            return;
        }

        const t = window.setTimeout(() => {
            setBillingProfileCountdown((c) => c - 1);
        }, 1000);
        return () => window.clearTimeout(t);
    }, [showBillingProfileModal, billingProfileCountdown, goToProfileForBillingGap]);

    const handleClose = (next: boolean) => {
        if (!next) {
            if (nonDismissable && step !== "success") {
                return;
            }

            if (step !== "success" && upgradingFromBasicTrial) {
                setPlanIntent(SubscriptionPlanIds.basic);
            }
            closeCheckout();
        }
    };

    const handleLogout = async () => {
        await logout();
        closeCheckout();
    };

    const showBillingProfileRequired = (gaps: CardBillingProfileGap[]) => {
        setBillingProfileGaps(gaps);
        setShowBillingProfileModal(true);
        setBillingProfileCountdown(10);
    };

    const cancelTrialUpgrade = () => {
        setPlanId(effectiveCurrentPlanId);
        setPlanIntent(SubscriptionPlanIds.basic);
        setStep("plan");
    };

    const confirmTrialUpgrade = () => {
        setStep("method");
    };

    const invalidateBilling = async () => {
        await refreshMe();
        await queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
        await queryClient.invalidateQueries({ queryKey: ["clinic", "subscription"] });
    };

    const startPix = async () => {
        setLoading(true);
        try {
            captureProductEvent("subscription_checkout_started", {
                plan_slug: resolveAnalyticsPlanSlug(planId) ?? "other",
                payment_method_kind: "pix",
            });
            if (isManage || isPaymentMethodOnly) {
                if (!useManageBillingApis) {
                    const result = await checkoutPixBilling(useClinicBilling, planId);
                    setPixSessionId(result.sessionId);
                    setPixPayload(result.qrPayload);
                    setDevSimulatePixAvailable(Boolean(result.devSimulatePixAvailable));
                    setStep("pix");
                    return;
                }

                const result = await changeBillingPaymentMethodBilling(useClinicBilling, {
                    paymentMethod: "pix_automatic",
                    planId: isPaymentMethodOnly ? undefined : planId,
                });
                await invalidateBilling();
                if (result.status === "pending" && result.sessionId && result.qrPayload) {
                    setPixSessionId(result.sessionId);
                    setPixPayload(result.qrPayload);
                    setDevSimulatePixAvailable(Boolean(billing?.devSimulatePixAvailable));
                    setStep("pix");
                    return;
                }
                setStep("success");
                toast.success(getTranslation(T.paymentMethodChanged));
                return;
            }

            const result = await checkoutPixBilling(useClinicBilling, planId);
            setPixSessionId(result.sessionId);
            setPixPayload(result.qrPayload);
            setDevSimulatePixAvailable(Boolean(result.devSimulatePixAvailable));
            setStep("pix");
        } catch (e: unknown) {
            toast.error(describeApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const submitCard = async () => {
        setLoading(true);
        try {
            const profile = (await refreshMe()) ?? user;

            if (!hasCardBillingProfile(profile)) {
                showBillingProfileRequired(getCardBillingProfileGaps(profile));
                return;
            }

            const cardPayload = {
                ...card,
                number: card.number.replace(/\D/g, ""),
            };
            const holderPayload = buildCreditCardHolderPayload(profile, cardPayload);

            captureProductEvent("subscription_checkout_started", {
                plan_slug: resolveAnalyticsPlanSlug(planId) ?? "other",
                payment_method_kind: "card",
            });

            if (isManage || isPaymentMethodOnly) {
                if (!useManageBillingApis) {
                    await checkoutCreditCardBilling(useClinicBilling, { planId, card: cardPayload, holder: holderPayload });
                    skipPendingCheckoutAutoOpenRef.current = true;
                    await invalidateBilling();
                    setStep("success");
                    toast.success(getTranslation(T.subscriptionActivated));
                    closeCheckout();
                    return;
                }

                await changeBillingPaymentMethodBilling(useClinicBilling, {
                    paymentMethod: "credit_card",
                    planId: isPaymentMethodOnly ? undefined : planId,
                    card: cardPayload,
                    holder: holderPayload,
                });
                skipPendingCheckoutAutoOpenRef.current = true;
                await invalidateBilling();
                setStep("success");
                toast.success(getTranslation(T.paymentMethodChanged));
                closeCheckout();
                return;
            }

            await checkoutCreditCardBilling(useClinicBilling, { planId, card: cardPayload, holder: holderPayload });
            skipPendingCheckoutAutoOpenRef.current = true;
            await invalidateBilling();
            setStep("success");
            toast.success(getTranslation(T.subscriptionActivated));
            closeCheckout();
        } catch (e: unknown) {
            const gatewayGaps = getCardBillingProfileGapsFromGatewayError(e);
            if (gatewayGaps.length > 0) {
                showBillingProfileRequired(gatewayGaps);
                return;
            }
            toast.error(describeApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const planChangeRouting = {
        hasActivePaidSubscription,
        currentPlanId: effectiveCurrentPlanId,
        targetPlanId: planId,
    };

    const submitPlanChange = async () => {
        if (!hasActivePaidSubscription && isUpgradePlan(effectiveCurrentPlanId, planId)) {
            setStep("method");
            return;
        }

        setLoading(true);
        try {
            const result = (
                await clinicApi.changeSubscriptionPlan({
                    planId,
                    selectedSecretaryUserId,
                })
            ).data;
            await invalidateBilling();
            await queryClient.invalidateQueries({ queryKey: ["clinic", "subscription"] });
            await queryClient.invalidateQueries({ queryKey: planAdjustmentNoticeQueryKey });
            // BR-MDR-43: refresh session before routing so activeClinicId is not stale.
            await refreshMe();
            if (result.proratedChargeBrl && result.proratedChargeBrl > 0) {
                toast.success(
                    getTranslation(T.planUpdatedWithProration).replace("{amount}", formatBrl(result.proratedChargeBrl)),
                );
            } else {
                toast.success(getTranslation(T.planUpdatedSuccess));
            }
            if (result.reactivated) {
                toast.success(getTranslation(T.subscriptionReactivated));
            }
            const landingPath = resolvePostPlanChangeLanding({
                previousPlanId: effectiveCurrentPlanId,
                targetPlanId: planId,
            });
            if (landingPath) {
                closeCheckout();
                navigate(landingPath, { replace: true });
                return;
            }
            setStep("success");
        } catch (e: unknown) {
            const ax = e as { response?: { data?: { blockers?: ClinicPlanFitBlocker[] } } };
            const blockers = ax.response?.data?.blockers;
            if (Array.isArray(blockers) && blockers.length > 0) {
                // BR-CCF-11 / FR-CCF-06: surface change-plan blockers as remediation, not toast-only.
                setRemediationBlockers(blockers);
                setRemediationOpen(true);
                return;
            }

            toast.error(describeApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const handlePlanContinue = async () => {
        if (paymentLock && planId === SubscriptionPlanIds.basic) {
            toast.error(getTranslation(T.completePaymentToContinue));
            return;
        }

        if (upgradingFromBasicTrial) {
            setStep("trialUpgradeConfirm");
            return;
        }

        if (upgradingFromFreeBasicTrial) {
            setStep("freeBasicTrialConfirm");
            return;
        }

        if (isManage) {
            if (changePaymentMethod) {
                setStep("method");
                return;
            }

            if (
                isUpgradePlan(effectiveCurrentPlanId, planId)
                && planId === SubscriptionPlanIds.clinic
                && !user?.activeClinicId
            ) {
                // Básico/Pro→Multi: existing clinic setup steps; no seat-fit (BR-CCF-14, FR-CCF-07).
                setStep("clinicSetup");
                return;
            }

            if (planChanged) {
                if (isClinicKeepPlanDowngrade(effectiveCurrentPlanId, planId)) {
                    setFitBusy(true);
                    try {
                        const { data } = await clinicApi.checkPlanFit({
                            targetPlanId: planId,
                        });
                        if (!data.ok) {
                            setRemediationBlockers(data.blockers ?? []);
                            setRemediationOpen(true);
                            return;
                        }

                        advanceAfterClinicReady();
                    } catch (err: unknown) {
                        const ax = err as { response?: { data?: { detail?: unknown } } };
                        toast.error(describeApiError(ax) || getTranslation(PlanFitT.planFitCheckFailed));
                    } finally {
                        setFitBusy(false);
                    }
                    return;
                }

                // Básico/Pro→Multi (and other non-downgrade): confirm + setup; no seat-fit (BR-CCF-14).
                advanceAfterClinicReady();
                return;
            }
            return;
        }
        setStep("method");
    };

    const advanceAfterClinicReady = useCallback(
        (summary = soloSecretariesQuery.data) => {
            setStep(resolveStepAfterClinicReady(summary, planChangeRouting));
        },
        [soloSecretariesQuery.data, hasActivePaidSubscription, effectiveCurrentPlanId, planId],
    );

    const handleClinicSetupSubmit = async (payload: ClinicSetupPayload) => {
        setLoading(true);
        try {
            await clinicApi.setupClinic(payload);
            const refreshedUser = await refreshMe();
            setClinicSetupCompleted(true);
            const therapistUserId = refreshedUser?.id ?? user?.id;
            if (!therapistUserId) {
                setStep("confirm");
                return;
            }
            const summary = await fetchSoloSecretaryMigrationSummary(
                queryClient,
                therapistUserId,
                shouldUseClinicBilling(refreshedUser ?? user),
            );
            setStep(resolveStepAfterClinicReady(summary));
        } catch (e: unknown) {
            toast.error(describeApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const modalTitle =
        step === "success"
            ? getTranslation(T.modalTitleSuccess)
            : isManage || isPaymentMethodOnly
                ? getTranslation(T.modalTitleManage)
                : getTranslation(T.modalTitleSubscribe);
    const showAllPlansOnPlanStep = step === "plan" && isManage;

    return (
        <>
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent
                    className={`flex max-h-[min(92vh,960px)] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-3xl p-0 ${showAllPlansOnPlanStep ? "max-w-3xl sm:max-w-4xl" : "max-w-lg"}`}
                    data-testid="subscription-checkout-modal"
                    dismissable={!nonDismissable}
                >
                    <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
                        <DialogTitle>{modalTitle}</DialogTitle>
                    </DialogHeader>
                    <DialogBody>

                    {billingQuery.isLoading && open ? (
                        <p className="text-sm text-muted-foreground">{getTranslation(T.loadingSubscription)}</p>
                    ) : null}

                    {step === "plan" ? (
                        <div className={`grid gap-3 ${showAllPlansOnPlanStep ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"}`}>
                            {selectablePlans.map((plan) => {
                                const isCurrent = isManage && effectiveCurrentPlanId === plan.id;
                                return (
                                    <button
                                        key={plan.id}
                                        type="button"
                                        className={`rounded-2xl border p-4 text-left transition ${isCurrent && planId === plan.id
                                            ? "border-primary bg-muted"
                                            : planId === plan.id
                                                ? "border-primary bg-muted"
                                                : isCurrent
                                                    ? "border-border bg-muted/40"
                                                    : "border-border bg-card"
                                            }`}
                                        onClick={() => {
                                            userSelectedPlan.current = true;
                                            setPlanId(plan.id);
                                            if (!isManage || !hasActivePaidSubscription) {
                                                setPlanIntent(plan.id);
                                            }
                                        }}
                                        data-testid={`checkout-plan-${plan.id}`}
                                    >
                                        {isCurrent ? (
                                            <span className="mb-2 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                                                {getTranslation(T.currentPlanBadge)}
                                            </span>
                                        ) : null}
                                        <p className="font-display text-lg font-bold text-foreground">{plan.name}</p>
                                        <p className="mt-2 whitespace-nowrap text-2xl font-bold text-primary">
                                            {plan.priceLabel}
                                            <span className="text-sm font-semibold text-muted-foreground">{plan.periodLabel}</span>
                                        </p>
                                    </button>
                                );
                            })}

                            {showChangePaymentMethodOption ? (
                                <div className={`${showAllPlansOnPlanStep ? "sm:col-span-3" : "sm:col-span-2"} flex items-start gap-2 rounded-xl border border-border p-3`}>
                                    <input
                                        id="change-payment-method"
                                        type="checkbox"
                                        checked={changePaymentMethod}
                                        onChange={(e) => setChangePaymentMethod(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-border"
                                        data-testid="checkout-change-payment-method"
                                    />
                                    <Label htmlFor="change-payment-method" className="text-sm leading-snug cursor-pointer">
                                        {pixMethodEnabled
                                            ? getTranslation(T.changePaymentMethodLabelPixOrCard)
                                            : getTranslation(T.changePaymentMethodLabelCardOnly)}
                                    </Label>
                                </div>
                            ) : null}

                            <Button
                                className={`${showAllPlansOnPlanStep ? "sm:col-span-3" : "sm:col-span-2"} w-full`}
                                onClick={() => void handlePlanContinue()}
                                disabled={continueDisabled}
                                isLoading={fitBusy}
                            >
                                {getTranslation(T.continue)}
                            </Button>
                        </div>
                    ) : null}

                    {step === "trialUpgradeConfirm" ? (
                        <TrialPlanUpgradeConfirmStep
                            currentPlanId={effectiveCurrentPlanId}
                            targetPlanId={planId}
                            onCancel={cancelTrialUpgrade}
                            onConfirm={confirmTrialUpgrade}
                        />
                    ) : null}

                    {step === "freeBasicTrialConfirm" ? (
                        <FreeBasicTrialConfirmStep
                            onCancel={() => setStep("plan")}
                            onConfirm={() => setStep("method")}
                        />
                    ) : null}

                    {step === "clinicSetup" ? (
                        <ClinicSetupStep
                            userName={user?.name ?? ""}
                            userEmail={user?.email ?? ""}
                            loading={loading}
                            onBack={() => setStep("plan")}
                            onSubmit={handleClinicSetupSubmit}
                        />
                    ) : null}

                    {step === "secretaryMigration" ? (
                        <SecretaryMigrationStep
                            summary={soloSecretariesQuery.data}
                            selectedSecretaryUserId={selectedSecretaryUserId}
                            onSelectSecretary={setSelectedSecretaryUserId}
                            onBack={() => setStep(resolveSecretaryMigrationBackStep(clinicSetupCompleted))}
                            onContinue={() => setStep(resolveStepAfterSecretaryMigration(planChangeRouting))}
                        />
                    ) : null}

                    {step === "confirm" ? (
                        <PlanChangeConfirmStep
                            currentPlanName={getPlanFromCatalog(catalog, effectiveCurrentPlanId).name}
                            targetPlanName={selectedPlan.name}
                            isDowngrade={isDowngradePlan(effectiveCurrentPlanId, planId)}
                            isUpgrade={isUpgradePlan(effectiveCurrentPlanId, planId)}
                            clinicRemains={isClinicKeepPlanDowngrade(effectiveCurrentPlanId, planId)}
                            subscriptionCancelled={Boolean(billing?.subscriptionCancelledAt)}
                            loading={loading}
                            onBack={() => setStep("plan")}
                            onConfirm={() => void submitPlanChange()}
                        />
                    ) : null}

                    {step === "method" ? (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                {getTranslation(T.methodStepPlanLabel)}{" "}
                                <strong className="text-secondary-foreground">{displayPlan.name}</strong> ({displayPlan.priceLabel}
                                {displayPlan.periodLabel})
                            </p>
                            {useManageBillingApis ? (
                                <p className="text-xs text-muted-foreground">
                                    {getTranslation(T.methodStepPaymentMethodProration)}
                                </p>
                            ) : upgradingFromBasicTrial ? (
                                <p className="text-xs text-muted-foreground">
                                    {getTranslation(T.methodStepTrialPayment).replace("{planName}", displayPlan.name)}
                                </p>
                            ) : null}
                            <div className="grid gap-3 sm:grid-cols-2">
                                <button
                                    type="button"
                                    className={`rounded-2xl border border-border p-4 text-left ${pixMethodEnabled
                                        ? "hover:border-border"
                                        : "cursor-not-allowed opacity-50"
                                        }`}
                                    onClick={() => {
                                        if (!pixMethodEnabled) {
                                            return;
                                        }
                                        void startPix();
                                    }}
                                    disabled={loading || !pixMethodEnabled}
                                    aria-disabled={!pixMethodEnabled}
                                    data-testid="checkout-method-pix"
                                >
                                    <PixLogoTile />
                                    <p className="mt-3 font-semibold text-foreground">{getTranslation(T.pixAutomaticTitle)}</p>
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {pixMethodEnabled
                                            ? getTranslation(T.pixAutomaticDescription)
                                            : upgradingFromFreeBasicTrial
                                                ? getTranslation(T.pixCardOnlyUpgrade)
                                                : getTranslation(T.pixUnavailableUntilAsaas)}
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    className="rounded-2xl border border-border p-4 text-left hover:border-border"
                                    onClick={() => setStep("card")}
                                    data-testid="checkout-method-card"
                                >
                                    <CreditCard className="h-10 w-10 text-secondary-foreground" aria-hidden />
                                    <p className="mt-3 font-semibold text-foreground">{getTranslation(T.creditCardTitle)}</p>
                                    <p className="mt-1 text-xs text-muted-foreground">{getTranslation(T.creditCardDescription)}</p>
                                </button>
                            </div>
                            {(isManage || isPaymentMethodOnly) && !isPaymentMethodOnly ? (
                                <Button variant="ghost" className="w-full" onClick={() => setStep("plan")}>
                                    {getTranslation(T.back)}
                                </Button>
                            ) : null}
                        </div>
                    ) : null}

                    {step === "pix" && pixPayload ? (
                        <div className="space-y-4 text-center" data-testid="checkout-pix-step">
                            <div className="mx-auto flex w-fit rounded-xl bg-card p-4 ring-1 ring-foreground/10">
                                <QRCodeSVG value={pixPayload} size={180} />
                            </div>
                            <p className="text-sm text-muted-foreground">{getTranslation(T.pixScanQrCode)}</p>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                onClick={async () => {
                                    await navigator.clipboard.writeText(pixPayload);
                                    toast.success(getTranslation(T.pixCodeCopied));
                                }}
                            >
                                {getTranslation(T.pixCopyCode)}
                            </Button>
                            {devSimulatePixAvailable && pixSessionId ? (
                                <>
                                    <p className="text-xs text-muted-foreground">
                                        {getTranslation(T.pixDevNotAutoConfirmed)}
                                    </p>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        className="w-full"
                                        data-testid="checkout-pix-simulate-btn"
                                        onClick={async () => {
                                            await simulatePixCheckoutBilling(useClinicBilling, pixSessionId);
                                            await invalidateBilling();
                                            setStep("success");
                                            toast.success(getTranslation(T.pixPaymentSimulated));
                                        }}
                                    >
                                        {getTranslation(T.pixSimulatePayment)}
                                    </Button>
                                </>
                            ) : (
                                <p className="text-xs text-muted-foreground">{getTranslation(T.pixAwaitingConfirmation)}</p>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                data-testid="checkout-pix-back"
                                onClick={() => {
                                    setPixSessionId(null);
                                    setPixPayload(null);
                                    setStep("method");
                                }}
                            >
                                {getTranslation(T.back)}
                            </Button>
                        </div>
                    ) : null}

                    {step === "card" ? (
                        <div className="space-y-3" data-testid="checkout-card-step">
                            <PaymentInput
                                label={getTranslation(T.cardNumberLabel)}
                                value={card.number}
                                onChange={(value) => setCard((c) => ({ ...c, number: value }))}
                            />
                            <div className="grid gap-3 sm:grid-cols-2">
                                <CardExpiryInput
                                    data-testid="checkout-card-expiry"
                                    value={cardExpiryDigits}
                                    onChange={(digits) => {
                                        setCardExpiryDigits(digits);
                                        const { expiryMonth, expiryYear } = parseCardExpiryParts(digits);
                                        setCard((c) => ({ ...c, expiryMonth, expiryYear }));
                                    }}
                                />
                                <Input
                                    placeholder={getTranslation(T.cardCvvPlaceholder)}
                                    value={card.ccv}
                                    onChange={(e) => setCard((c) => ({ ...c, ccv: e.target.value }))}
                                />
                            </div>
                            <Input
                                placeholder={getTranslation(T.cardHolderNamePlaceholder)}
                                value={card.holderName}
                                onChange={(e) => setCard((c) => ({ ...c, holderName: e.target.value }))}
                            />
                            <Input
                                placeholder={getTranslation(T.cardHolderCpfPlaceholder)}
                                value={holder.cpfCnpj}
                                onChange={(e) => setHolder((h) => ({ ...h, cpfCnpj: e.target.value }))}
                            />
                            <Button className="w-full" isLoading={loading} onClick={() => void submitCard()}>
                                {getTranslation(T.confirmPayment)}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                data-testid="checkout-card-back"
                                onClick={() => setStep("method")}
                            >
                                {getTranslation(T.back)}
                            </Button>
                        </div>
                    ) : null}

                    {step === "success" ? (
                        <div className="space-y-4 text-sm text-muted-foreground">
                            <p>
                                {getTranslation(T.successActiveMessage).replace("{planName}", selectedPlan.name)}
                            </p>
                            {devFakeBillingAvailable && !useClinicBilling && (user?.trialDaysRemaining ?? 0) > 0 ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full"
                                    data-testid="checkout-simulate-trial-charge"
                                    disabled={loading}
                                    onClick={async () => {
                                        setLoading(true);
                                        try {
                                            await clinicApi.simulateTrialChargeCheckout();
                                            await invalidateBilling();
                                            await refreshMe();
                                            toast.success(getTranslation(T.postTrialChargeSimulated));
                                        } catch (e: unknown) {
                                            toast.error(describeApiError(e));
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                >
                                    {getTranslation(T.simulatePostTrialCharge)}
                                </Button>
                            ) : null}
                            <Button className="w-full" onClick={() => closeCheckout()}>
                                {getTranslation(T.close)}
                            </Button>
                        </div>
                    ) : null}

                    {nonDismissable && (step === "plan" || step === "method") ? (
                        <div className="pt-4 space-y-1">
                            <Button
                                type="button"
                                variant="ghost"
                                className="w-full"
                                data-testid="checkout-back-btn"
                                onClick={() => closeCheckout()}
                            >
                                {getTranslation(T.goBack)}
                            </Button>
                        </div>
                    ) : null}
                    </DialogBody>
                </DialogContent>
            </Dialog>

            <Dialog open={showBillingProfileModal} onOpenChange={setShowBillingProfileModal}>
                <DialogContent className="flex max-h-[min(92vh,960px)] w-[calc(100vw-2rem)] max-w-md flex-col gap-0 overflow-hidden rounded-3xl p-0" data-testid="checkout-billing-profile-required-modal">
                    <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
                        <DialogTitle>{getTranslation(T.billingProfileIncompleteTitle)}</DialogTitle>
                    </DialogHeader>
                    <DialogBody className="space-y-3">
                        <p className="text-sm text-secondary-foreground">{getTranslation(T.billingProfileCompletePrompt)}</p>
                        <p className="text-sm font-semibold text-foreground" data-testid="checkout-billing-profile-gaps">
                            {formatCardBillingProfileGapsList(billingProfileGaps)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                        {getTranslation(T.billingProfileRedirectCountdown).replace(
                            "{seconds}",
                            String(billingProfileCountdown),
                        )}
                    </p>
                    </DialogBody>
                    <DialogFooter className="mx-0 mb-0 gap-2 sm:gap-3">
                        <Button onClick={goToProfileForBillingGap} className="w-full">
                            {getTranslation(T.goNow)}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowBillingProfileModal(false);
                            }}
                            className="w-full"
                        >
                            {getTranslation(T.close)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={remediationOpen}
                onOpenChange={(next) => {
                    if (!next) {
                        setRemediationOpen(false);
                        setRemediationBlockers([]);
                    }
                }}
            >
                <DialogContent
                    className="flex max-h-[min(92vh,960px)] w-[calc(100vw-2rem)] max-w-md flex-col gap-0 overflow-hidden rounded-3xl p-0"
                    data-testid="subscription-manage-plan-fit-remediation"
                >
                    <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
                        <DialogTitle>
                            {getTranslation(PlanFitT.planFitRemediationTitle)}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogBody className="space-y-3">
                        <p className="text-sm text-primary leading-relaxed">
                            {getTranslation(PlanFitT.planFitRemediationHint)}
                        </p>
                        <ul className="list-disc space-y-2 pl-5 text-sm text-primary">
                            {remediationBlockers.map((blocker) => (
                                <li
                                    key={`${blocker.code}-${blocker.detail}`}
                                    data-testid={`subscription-manage-plan-fit-blocker-${blocker.code}`}
                                >
                                    {blocker.detail}
                                </li>
                            ))}
                        </ul>
                    </DialogBody>
                    <DialogFooter className="mx-0 mb-0 gap-2 sm:gap-3">
                        <Button
                            type="button"
                            className="w-full sm:w-auto"
                            data-testid="subscription-manage-plan-fit-remediation-close"
                            onClick={() => {
                                setRemediationOpen(false);
                                setRemediationBlockers([]);
                            }}
                        >
                            {getTranslation(PlanFitT.planFitRemediationClose)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
